import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import Utils from '../../helpers/utils';
import {
    IPatientProfileSummary,
    IPatientProfiles,
    IPatientsInitialProfilePayload,
    IPatientsProfilePayload,
    IPatientsRegisterPayload,
} from '../../entities/Patients/Patient.ts';

export class PatientsApi extends BaseApi {
    private static baseApiUrl: string = `${BaseApi.apiUrl}/Patients`;
    public static apiUrls: {
        getPatients: string;
        getCurrentProfiles: string;
        postPatient: string;
        getPatientById: string;
        getPatientProfileSummary: string;
        putPatientInitialProfile: string;
        putPatientProfile: string;
        getPatientInvitationToken: string;
        putJournalInactivityReminder: string;
        getJournalInactivitySettings: string;
        putFileNumber: string;
        putBirthDate: string;
        putSex: string;
        putJournalInactivitySettings: string;
    } = {
        getPatients: `${PatientsApi.baseApiUrl}`,
        getCurrentProfiles: `${PatientsApi.baseApiUrl}/Current/Profile`,
        postPatient: `${PatientsApi.baseApiUrl}`,
        getPatientById: `${PatientsApi.baseApiUrl}/:PatientId`,
        getPatientProfileSummary: `${PatientsApi.baseApiUrl}/:PatientId/ProfileSummary`,
        putPatientInitialProfile: `${PatientsApi.baseApiUrl}/Current/InitialProfile`,
        putPatientProfile: `${PatientsApi.baseApiUrl}/Current/Profile`,
        getPatientInvitationToken: `${PatientsApi.baseApiUrl}/Current/InvitationToken`,
        putJournalInactivityReminder: `${PatientsApi.baseApiUrl}/:id/journal-inactivity-reminder`,
        getJournalInactivitySettings: `${PatientsApi.baseApiUrl}/:patientId/journal-inactivity-settings/:journalId`,
        putFileNumber: `${PatientsApi.baseApiUrl}/:id/FileNumber`,
        putBirthDate: `${PatientsApi.baseApiUrl}/:id/BirthDate`,
        putSex: `${PatientsApi.baseApiUrl}/:id/Sex`,
        putJournalInactivitySettings: `${PatientsApi.baseApiUrl}/:patientId/journal-inactivity-settings/:journalId`,
    };

    public static async getPatientProfileSummary(patientId: string): Promise<IPatientProfileSummary> {
        return this.sendApiRequest<IPatientProfileSummary>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getPatientProfileSummary.replace(':PatientId', patientId),
        });
    }

    public static async updateFileNumber(patientId: string, fileNumber: string | null): Promise<boolean> {
        return this.sendApiRequest<boolean>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.putFileNumber.replace(':id', patientId),
            data: { fileNumber },
        });
    }

    public static async updateBirthDate(patientId: number, birthDate: Date): Promise<boolean> {
        return this.sendApiRequest<boolean>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.putBirthDate.replace(':id', patientId.toString()),
            data: { birthDate: Utils.formatLocalDateToYYYYMMDD(birthDate) },
        });
    }

    public static async updateSex(patientId: number, sexID: number): Promise<boolean> {
        return this.sendApiRequest<boolean>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.putSex.replace(':id', patientId.toString()),
            data: { sexID },
        });
    }

    public static async getPatientsProfiles(): Promise<IPatientProfiles> {
        return this.sendApiRequest<IPatientProfiles>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getCurrentProfiles,
        });
    }

    public static async postPatient(data: IPatientsRegisterPayload): Promise<number> {
        return this.sendApiRequest<number>({ 
            method: HttpMethodEnum.POST, 
            path: this.apiUrls.postPatient, 
            data: data,
            suppressGlobalErrorHandling: true
        });
    }

    public static async putPatientInitialProfile(data: IPatientsInitialProfilePayload): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.putPatientInitialProfile,
            data: data,
        });
    }

    public static async putPatientProfile(data: IPatientsProfilePayload): Promise<number> {
        return this.sendApiRequest<number>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.putPatientProfile,
            data: data,
        });
    }

    public static async getPatientInvitationToken(): Promise<string> {
        return this.sendApiRequest<string>({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getPatientInvitationToken,
        });
    }

    public static async updateJournalInactivityReminder(patientId: number, isDisabled: boolean): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.putJournalInactivityReminder.replace(':id', patientId.toString()),
            data: { isDisabled } as any,
        });
    }

    public static async getJournalInactivitySettings(patientId: number, journalId: number): Promise<any> {
        return this.sendApiRequest({
            method: HttpMethodEnum.GET,
            path: this.apiUrls.getJournalInactivitySettings
                .replace(':patientId', patientId.toString())
                .replace(':journalId', journalId.toString()),
        });
    }

    public static async updateJournalInactivitySettings(patientId: number, journalId: number, isActive: boolean): Promise<void> {
        return this.sendApiRequest<void>({
            method: HttpMethodEnum.PUT,
            path: this.apiUrls.putJournalInactivitySettings
                .replace(':patientId', patientId.toString())
                .replace(':journalId', journalId.toString()),
            data: { isActive } as any,
        });
    }
}
